import { Countries, ProfessionalInterestSurvey } from '@platform/client';
import {
  SurveyCoverageEnum,
  SurveyInvestmentEnum,
  SurveyOutcomeEnum,
  SurveyTriggerEnum,
} from '@platform/client/schemas';
import {
  Button,
  ErrorBox,
  FormsCheckBox,
  FormsInputDropdown,
  FormsMultilineInput,
  InlineFormInput,
  RadioGroup,
  TagSelect,
} from '@platform/components';
import mixpanel from 'mixpanel-browser';
import { PropsWithChildren } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import EVENTS from '../../../analytics/events';
import { MIXPANEL_ENABLED } from '../../../analytics/mixpanel';
import { useConsent } from '../../../analytics/useConsent';

type FormValues = {
  trigger: SurveyTriggerEnum;
  triggerOther: string;
  outcome: SurveyOutcomeEnum;
  outcomeReason: string;
  investment: SurveyInvestmentEnum;
  coverage: SurveyCoverageEnum;
  currentEmployer: string;
  jobTitle: string;
  country: string;
  city: string;
  email: string;
  newsletterOptIn: boolean;
};

const TRIGGER_OPTIONS = [
  { value: SurveyTriggerEnum.pivot, title: 'I want to move into social impact or CSR work' },
  {
    value: SurveyTriggerEnum.grow_in_role,
    title: 'I want to gain hands-on impact experience alongside my current career',
  },
  {
    value: SurveyTriggerEnum.between_roles,
    title: 'I’m between roles and want to keep building useful skills',
  },
  { value: SurveyTriggerEnum.give_back, title: 'I want to give back and create meaningful impact' },
  { value: SurveyTriggerEnum.other, title: 'Something else' },
];

const OUTCOME_OPTIONS = [
  {
    value: SurveyOutcomeEnum.community,
    title: 'A network of purpose-driven professionals learning and growing together',
  },
  {
    value: SurveyOutcomeEnum.practice,
    title:
      'Experience in impact-driven startup environments that builds your ability to adapt, problem-solve, and lead',
  },
  {
    value: SurveyOutcomeEnum.credential,
    title: 'A credential and portfolio of impact work you can share to advance your career',
  },
];

const INVESTMENT_OPTIONS = [
  { value: SurveyInvestmentEnum.under_150, label: 'Under $150' },
  { value: SurveyInvestmentEnum['150_500'], label: '$150–$500' },
  { value: SurveyInvestmentEnum['500_900'], label: '$500–$900' },
  { value: SurveyInvestmentEnum['900_2500'], label: '$900–$2,500' },
  { value: SurveyInvestmentEnum['2500_5000'], label: '$2,500–$5,000' },
  { value: SurveyInvestmentEnum['5000_plus'], label: '$5,000+' },
  { value: SurveyInvestmentEnum.none_recent, label: 'Nothing recently' },
];

const COVERAGE_OPTIONS = [
  { value: SurveyCoverageEnum.self, title: 'I paid for it myself' },
  {
    value: SurveyCoverageEnum.stipend,
    title: 'I used a training budget or stipend from my employer',
  },
  { value: SurveyCoverageEnum.employer, title: 'I requested dedicated budget from my employer' },
  { value: SurveyCoverageEnum.shared, title: 'My employer and I shared the cost' },
];

const QuestionLegend = ({ number, children }: PropsWithChildren<{ number: number }>) => (
  <legend className='flex items-start gap-3 text-xl font-semibold text-gray-700 mb-[18px] p-0'>
    <span className='w-7 h-7 rounded-full bg-secondary-brand-25 border border-gray-100 text-brand-500 flex items-center justify-center text-sm font-semibold flex-shrink-0'>
      {number}
    </span>
    <span className='pt-0.5'>{children}</span>
  </legend>
);

export const SurveyForm = ({ onSubmitSuccess }: { onSubmitSuccess: () => void }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const countriesList = Countries.useGetCountries();
  const countryOptions =
    countriesList.isSuccess ?
      countriesList.data.map((country) => ({
        text: country.name,
        textValue: country.id.toString(),
      }))
    : [];

  const { consent } = useConsent();

  const submitSurvey = ProfessionalInterestSurvey.useSubmitProfessionalInterestSurvey({
    mutation: {
      onSuccess: () => {
        if (MIXPANEL_ENABLED && consent.analytics) {
          mixpanel.track(EVENTS.SIGN_UP.PROFESSIONAL_PAUSE.SURVEY_SUBMITTED);
        }
        onSubmitSuccess();
      },
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    submitSurvey.mutate({
      data: {
        email: data.email,
        trigger: data.trigger,
        triggerOther: data.trigger === SurveyTriggerEnum.other ? data.triggerOther || null : null,
        outcome: data.outcome,
        outcomeReason: data.outcomeReason || null,
        investment: data.investment,
        coverage: data.coverage,
        currentEmployer: data.currentEmployer || null,
        jobTitle: data.jobTitle || null,
        countryId: data.country ? Number(data.country) : null,
        city: data.city || null,
        newsletterOptIn: !!data.newsletterOptIn,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      noValidate
      aria-label='Perspective survey'
      className='max-w-[760px] mx-auto text-left bg-white border border-gray-100 rounded-2xl shadow-sm px-2 sm:px-11 py-10 flex flex-col gap-8 text-gray-800'
    >
      {submitSurvey.isError && (
        <ErrorBox showAlertTriangle variant='compact'>
          Something went wrong submitting your response. Please try again.
        </ErrorBox>
      )}
      {isSubmitted && Object.keys(errors).length > 0 && (
        <ErrorBox showAlertTriangle variant='compact'>
          Answer all required questions before submitting.
        </ErrorBox>
      )}

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={1}>What’s bringing you to MovingWorlds right now?</QuestionLegend>
        <RadioGroup
          name='trigger'
          aria-label='trigger'
          variant='cards'
          items={TRIGGER_OPTIONS}
          onChange={(value) =>
            setValue('trigger', value as SurveyTriggerEnum, { shouldValidate: true })
          }
          register={() =>
            register('trigger', { required: 'Select what’s bringing you to MovingWorlds.' })
          }
          defaultValue={watch('trigger')}
          error={errors.trigger?.message}
        />
        {watch('trigger') === SurveyTriggerEnum.other && (
          <InlineFormInput
            label='What brings you here?'
            hideLabel
            placeholder='What brings you here?'
            register={() =>
              register('triggerOther', { required: 'Please tell us what brings you here.' })
            }
            errors={errors.triggerOther}
          />
        )}
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={2}>What do you most hope to get from MovingWorlds?</QuestionLegend>
        <RadioGroup
          name='outcome'
          aria-label='outcome'
          variant='cards'
          items={OUTCOME_OPTIONS}
          onChange={(value) =>
            setValue('outcome', value as SurveyOutcomeEnum, { shouldValidate: true })
          }
          register={() =>
            register('outcome', { required: 'Select what you most hope to get from MovingWorlds.' })
          }
          defaultValue={watch('outcome')}
          error={errors.outcome?.message}
        />
        <FormsMultilineInput
          control={control}
          name='outcomeReason'
          label='Anything you’re hoping for that we haven’t named? — optional'
          placeholder='Tell us in your own words'
        />
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={3}>
          How much did you last spend on your professional growth?
        </QuestionLegend>
        <TagSelect
          multiple={false}
          errorVariant='compact'
          tags={INVESTMENT_OPTIONS}
          selectedTags={watch('investment') ? [watch('investment')] : []}
          register={() => register('investment', { required: 'Select a spend range.' })}
          error={errors.investment?.message}
          onSelectionChange={(value) =>
            setValue('investment', value[0] as SurveyInvestmentEnum, { shouldValidate: true })
          }
        />
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={4}>Who covered the cost?</QuestionLegend>
        <RadioGroup
          name='coverage'
          aria-label='coverage'
          variant='cards'
          items={COVERAGE_OPTIONS}
          onChange={(value) =>
            setValue('coverage', value as SurveyCoverageEnum, { shouldValidate: true })
          }
          register={() => register('coverage', { required: 'Tell us who covered the cost.' })}
          defaultValue={watch('coverage')}
          error={errors.coverage?.message}
        />
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={5}>Where do you currently work?</QuestionLegend>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-[18px]'>
          <InlineFormInput
            label='Current employer — or self-employed'
            placeholder='Enter your company name'
            register={() =>
              register('currentEmployer', { required: 'Enter your current employer.' })
            }
            errors={errors.currentEmployer}
          />
          <InlineFormInput
            label='Job title — or your most recent'
            placeholder='e.g. Director of Operations'
            register={() => register('jobTitle', { required: 'Enter your job title.' })}
            errors={errors.jobTitle}
          />
        </div>
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={6}>Where are you currently based?</QuestionLegend>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-[18px]'>
          <FormsInputDropdown
            control={control}
            name='country'
            rules={{ required: 'Select your country.' }}
            label='Country'
            placeholder='Start typing country name'
            items={countryOptions}
            type='default'
            error={errors.country}
          />
          <InlineFormInput
            label='City — optional'
            placeholder='Enter your city'
            register={() => register('city')}
            errors={errors.city}
          />
        </div>
      </fieldset>

      <fieldset className='border-0 p-0 m-0 flex flex-col gap-[18px]'>
        <QuestionLegend number={7}>Add your email</QuestionLegend>
        <InlineFormInput
          label="Email — we'll use it to show you what your feedback helped build"
          inputType='email'
          placeholder='you@example.com'
          register={() =>
            register('email', {
              required: 'Add your email address.',
              pattern: { value: /^\S+@\S+$/i, message: 'Please enter a valid email address.' },
            })
          }
          errors={errors.email}
        />
        <FormsCheckBox control={control} name='newsletterOptIn' size='md'>
          Send me monthly updates: career opportunities, community news, and impact stories.
        </FormsCheckBox>
      </fieldset>

      <div>
        <Button variant='primary' size='xl' type='submit' isDisabled={submitSurvey.isPending}>
          {submitSurvey.isPending ? 'Sharing…' : 'Share my perspective'}
        </Button>
      </div>
    </form>
  );
};
